import JwtDecode from 'jwt-decode'

export default class User {
    static from(token) {
        try {
            const obj = JwtDecode(token)
            return new User(Object.assign(obj, {isLoggedIn: true}))
        } catch (_) {
            return User.loggedOutUser()
        }
    }

    static loggedOutUser() {
        return new User({}) // loggedOut user
    }

    constructor({user_id, exp, isLoggedIn, impersonation_session_id}) {
        this.id = user_id // eslint-disable-line camelcase
        this.impersonationSessionId = impersonation_session_id // eslint-disable-line camelcase
        this.first_name = ''
        this.last_name = ''
        this.email = ''
        this.active = ''
        this.password_change_supported = false
        this.deletion_request_pending = false
        this.exp = exp
        this.groups = []
        this.isLoggedIn = isLoggedIn
        this.isDataFetched = false
        this.surveyCompleted = false
        this.address = null
        this.invoice_address = null
        this.organisations = {
            // simple init from token
            accounts: [],
            // extended by fetchMyClients e.g.
            // 1234: [org1, org2],
        };
        const currentOrg = this.organisations.accounts[0] || {}
        this.organisation = {...currentOrg}
    }
    updateUserData(data) {
        this.email = data.email;
        this.first_name = data.first_name;
        this.last_name = data.last_name;
        this.password_change_supported = !!data.password_change_supported;
        this.deletion_request_pending = !!data.deletion_request_pending;
        this.groups = data.groups;
        this.address = data.address || null;
        this.invoice_address = data.invoice_address || null;
        this.organisations.accounts = data.clients || [];
        this.surveyCompleted = this.surveyCompleted || this.organisations.accounts.length > 0;
        this.reconcileOrganisation(this.organisations.accounts);
        this.isDataFetched = true;
    }
    isCustomer() {
        return !this.groups.manetStaff && !this.groups.manetAdmin;
    }
    isStaffMember() {
        return this.groups.manetStaff || this.groups.manetAdmin
    }
    currentOrganisationPresent() {
        return this.organisation && !!this.organisation.kdnr;
    }

    hasActivePackage(baseType) {
        return (this.organisation?.active_packages || []).some((pkg) => pkg?.base_type === baseType)
    }

    canAccessMarketingRoute(route) {
        if (this.isStaffMember()) {
            return true
        }

        const requiredPackageByRoute = {
            'accommodations.index': 'unterkunft',
            'offers.index': 'angebote',
            'events.index': 'termine',
            'discover.index': 'entdecken',
        }

        const requiredPackage = requiredPackageByRoute[route]
        if (!requiredPackage) {
            return true
        }

        return this.hasActivePackage(requiredPackage)
    }

    getPackageLockedMenuItem(item) {
        if (!item?.route || !this.currentOrganisationPresent() || this.canAccessMarketingRoute(item.route)) {
            return item
        }

        return {
            ...item,
            disabled: true,
            disabledReason: 'package_required',
            disabledHintRoute: 'packages.index',
        }
    }

    // Admin2 records the visit when the list loads; this only hides the notice until /users/me is fetched again.
    markInquiriesSeen() {
        const kdnr = this.organisation?.kdnr
        this.organisation = {...this.organisation, has_new_inquiries: false}
        this.organisations.accounts = this.organisations.accounts.map(
            (account) => account.kdnr === kdnr ? {...account, has_new_inquiries: false} : account
        )
    }

    needsSurvey() {
        return this.organisations.accounts?.length === 0 && !this.surveyCompleted && !this.isStaffMember()
    }

    getAttentionReasons(route = null) {
        const routeReasons = {
            'account.survey': [
                this.needsSurvey() && 'survey_needed',
            ],
        }

        if (route) {
            return (routeReasons[route] || []).filter(Boolean)
        }

        return Object.values(routeReasons).flat().filter(Boolean)
    }

    needsAttention(route = null) {
        return this.getAttentionReasons(route).length > 0
    }

    markSurveyCompleted() {
        this.surveyCompleted = true
    }

    getCustomerMenuItems() {
        const marketingObjectItems = {
            accommodations:
                this.getPackageLockedMenuItem({
                    name: 'Unterkünfte',
                    route: 'accommodations.index',
                    icon: 'fa-pro-solid:house',
                }),
            offers:
                this.getPackageLockedMenuItem({
                    name: 'Angebote',
                    route: 'offers.index',
                    icon: 'fa-pro-solid:tags',
                }),
            events:
                this.getPackageLockedMenuItem({
                    name: 'Veranstaltungen',
                    route: 'events.index',
                    icon: 'fa-pro-solid:calendar-day',

                }),
            discover:
                this.getPackageLockedMenuItem({
                    name: 'Entdecken',
                    route: 'discover.index',
                    icon: 'fa-pro-solid:compass',
                }),
        }

        return {
            dashboard: {
                header: '',
                children: {
                    dashboard:
                        {
                            name: 'Dashboard',
                            route: 'dashboard',
                            icon: 'material-symbols:dashboard',
                        },
                }
            },
            vermarktung: {
                header: 'Ihre Vermarktung',
                disabled: !this.isStaffMember() && !this.currentOrganisationPresent(),
                children: {
                    ...marketingObjectItems,
                    inquiries:
                        {
                            name: 'Anfragen',
                            icon: 'fa-pro-solid:inbox',
                            route: 'inquiries.index',
                            hide: !this.organisation?.inquiries_enabled,
                            noticeIcon: this.organisation?.has_new_inquiries ? 'clarity:envelope-outline-badged' : null,
                        },
                    ads:
                        {
                            name: 'Werbeanzeigen',
                            icon: 'fa-pro-solid:megaphone',
                            route: 'ads.index',
                        },
                    websites:
                        {
                            name: 'Websites',
                            route: 'websites.index',
                            icon: 'fa-pro-solid:globe',
                        },
                    domains:
                        {
                            name: 'Domains',
                            route: 'domains.index',
                            icon: 'iconoir:www',
                        },
                    packages:
                        {
                            name: 'Pakete',
                            route: 'packages.index',
                            icon: 'streamline-ultimate:startup-product-rocket-box-bold',
                            highlight: true,
                        }
                }
            },
            account: {
                header: 'Ihr Konto',
                account: true,
                children: {
                    clients:
                        {
                            name: 'Organisationen',
                            route: 'organisations.index',
                            icon: 'fa-pro-solid:folder-tree',
                            // hide: !this.manetStaff,
                        },
                    media:
                        {
                            name: 'Fotos / Dokumente / Videos',
                            route: 'media.index',
                            icon: 'fa-pro-solid:folders',
                            disabled: !this.currentOrganisationPresent(),
                        },
                    settings:
                        {
                            name: 'Einstellungen',
                            route: 'account.settings',
                            icon: 'fa-pro-solid:gears',
                        },
                    invoices:
                        {
                            name: 'Rechnungen',
                            route: 'account.invoices',
                            icon: 'fa-pro-solid:file-invoice-dollar',
                        },
                    survey:
                        {
                            name: 'Fragebogen',
                            route: 'account.survey',
                            hide: !this.needsSurvey(),
                            icon: 'fa-pro-solid:clipboard-list-check',
                        },
                    support:
                      {
                          name: 'Support',
                          route: 'account.support',
                          icon: 'fa-pro-solid:comments-question',
                      }
                }

            },
        }
    }

    setOrganisation(org) {
        // console.debug(`this.setOrganisation(${JSON.stringify(kdnr)})`)
        if (!org || !org.kdnr) {
            this.organisation = {}
            return
        }
        const account = this.organisations.accounts.find(
            (candidate) => String(candidate.kdnr) === String(org.kdnr)
        ) || {}
        this.organisation = {...account, ...org}
        // this.clearObjects()
    }

    getOrganisations() {
        return this.organisations.accounts || [];
    }

    setCurrentOrganisations(orgs) {
        if (orgs && !this.organisation.kdnr && orgs.length > 0) {
            // auto set first organisation as current organisation
            this.setOrganisation(orgs[0])
        }
        return this.organisations[this.organisation.kdnr] = orgs;
    }

    getCurrentOrganisations(fallback = []) {
        if (this.organisation.kdnr) {
            return this.organisations[this.organisation.kdnr] || fallback;
        } else {
            return fallback;
        }
    }

    reconcileOrganisation(orgs = []) {
        if (!orgs.length) {
            this.setOrganisation(null)
            return
        }

        const currentKdnr = this.organisation?.kdnr
        const matchedOrg = orgs.find((org) => String(org.kdnr) === String(currentKdnr))
        if (matchedOrg) {
            this.setOrganisation(matchedOrg)
            return
        }

        this.setOrganisation(orgs[0])
    }
}
